import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	HomeIcon,
} from '@heroicons/react/20/solid';

import Banner from '../Banner';
import { Button } from '../Button';
import { classNames } from '../helpers/functions';
import LoadingSkeleton from './LoadingSkeleton';

export const MainContent = ({ ...props }) => {
	return <ContentStandard {...props} />;
};

const ContentWithBanners = ({ banners = [], ...props }) => {
	return (
		<div className="flex-1">
			<div></div>

			<div className="flex">
				<MainContent {...props} />
			</div>
		</div>
	);
};

const ContentStandard = ({
	sidebarLeft,
	sidebarRight,
	children,
	className,
	maxWidth = 'max-w-6xl',
	actions,
	border = true,
	sidebarProps,
	banners,
	fullWidth,
}) => {
	return (
		<div
			className={classNames(
				fullWidth ? '' : ' mx-auto max-w-(--breakpoint-2xl)',
				'p-4 py-6 sm:p-6 sm:px-8 md:p-10 md:px-12',
				maxWidth,
				className,
			)}
		>
			{children}
		</div>
	);

	return (
		<>
			{sidebarLeft && (
				<SecondarySidebar
					border={border ? 'border-r border-slate-200' : false}
					{...sidebarProps}
				>
					{sidebarLeft}
				</SecondarySidebar>
			)}

			<div className="flex-1 xl:overflow-y-auto">
				<div
					className={classNames(
						'p-4 mx-auto max-w-(--breakpoint-2xl) md:p-6',
						maxWidth,
						className,
					)}
				>
					{actions?.length ? (
						<PageHeading divider={false} actions={actions} />
					) : null}
					{children}
				</div>
			</div>

			{sidebarRight && (
				<SecondarySidebar border={border ? 'border-l border-slate-200' : false}>
					{sidebarRight}
				</SecondarySidebar>
			)}
		</>
	);
};

export const SecondarySidebar = ({
	children,
	border = 'border-r border-slate-200',
	paddingX = '10',
}) => {
	return (
		<div
			className={classNames(
				'hidden w-96 flex-shrink-0 xl:flex xl:flex-col pt-12 pb-4 xl:overflow-y-auto',
				`px-${paddingX}`,
				border,
			)}
		>
			{children}
		</div>
	);
};

export const PageHeading = ({
	children,
	actions = [],
	pages = [],
	divider = true,
	homePath = '/',
	onBack,
	breadcrumbs = [],
	forceBreadcrumbs = false,
	RightElement,
	isSubheading = false,
	gap,
	containerClassName,
}) => {
	const navigate = useNavigate();

	const Buttons = actions.map((action, i) => (
		<Button
			key={`action-btn-${i}`}
			type={action.type}
			onClick={action.onClick}
			className
			disabled={action.type === 'disabled' || action.disabled}
		>
			{action.name}
		</Button>
	));

	const GoBackButton =
		typeof onBack === 'function' ? (
			<Button
				type="ghost"
				className="ml-0 py-0 px-2 mr-6"
				onClick={() => onBack()}
			>
				<ChevronLeftIcon className="w-5 h-5" />
			</Button>
		) : null;

	const lastPage = pages?.length === 0;

	const GAPS = {
		lg: 'pb-6 mb-0 sm:mb-4',
		sm: 'py-2 pb-6',
	};

	return (
		<div
			className={classNames(
				divider ? 'border-b border-gray-200 ' : '',
				gap ? GAPS[gap] : GAPS.lg,
				'flex items-center justify-between',
				containerClassName,
			)}
		>
			<div>
				{breadcrumbs?.length || forceBreadcrumbs ? (
					<nav className="flex mb-3" aria-label="Breadcrumb">
						<ol role="list" className="flex items-center space-x-4">
							<li>
								<div>
									<div
										onClick={() => navigate('/')}
										className="text-gray-400 hover:text-gray-500 cursor-pointer"
									>
										<HomeIcon
											className="h-5 w-5 flex-shrink-0"
											aria-hidden="true"
										/>
										<span className="sr-only">Home</span>
									</div>
								</div>
							</li>
							{breadcrumbs.map((page) => (
								<li key={page.name}>
									<div className="flex items-center">
										<ChevronRightIcon
											className="h-5 w-5 flex-shrink-0 text-gray-400"
											aria-hidden="true"
										/>
										<div
											onClick={() => navigate(page.path)}
											className="ml-4 text-xs font-medium text-gray-400 hover:text-gray-500 cursor-pointer"
											aria-current={page.current ? 'page' : undefined}
										>
											{page.name}
										</div>
									</div>
								</li>
							))}
						</ol>
					</nav>
				) : null}
				<div className="flex items-center">
					{GoBackButton}
					<h3
						className={classNames(
							isSubheading ? 'text-md' : 'text-2xl',
							'font-semibold leading-6 text-gray-900 flex align-center tracking-tight',
						)}
					>
						<span
							className={classNames(
								lastPage ? '' : 'text-indigo-700 font-normal cursor-pointer',
							)}
							onClick={() => (lastPage ? null : navigate(homePath))}
						>
							{children}
						</span>
						{pages?.map((page, i) => {
							const isLastPage = pages.length - 1 === i;

							return (
								<div className="flex items-center">
									<ChevronRightIcon className="mx-5 w-5 h-5 text-gray-400" />{' '}
									<span
										className={classNames(
											isLastPage
												? ''
												: 'text-indigo-700 font-normal cursor-pointer',
										)}
										onClick={() => (!isLastPage ? navigate(page.path) : null)}
									>
										{page.name}
									</span>
								</div>
							);
						})}
					</h3>
				</div>
			</div>

			<div className="mt-0 sm:ml-4 flex flex-col sm:flex-row space-y-4 sm:space-y-0">
				{RightElement}
				{Buttons}
			</div>
		</div>
	);
};

export const Base = ({ children, className }) => (
	<main className="flex-1">
		<div className="py-6">
			<div
				className={classNames(
					'mx-auto px-4 sm:px-6 md:px-8 ',
					className || 'max-w-7xl',
				)}
			>
				{children}
			</div>
		</div>
	</main>
);

export const ErrorMessage = () => (
	<div className="text-center">
		<ExclamationTriangleIcon
			className="block h-6 w-6 text-red-700 mx-auto"
			aria-hidden="true"
		/>
		<h3 className="mt-2 text-sm font-medium text-gray-900">
			Uh oh, something went wrong
		</h3>
		<p className="mt-1 text-sm text-gray-500">
			Please try refreshing the page or contacting support
		</p>
	</div>
);

export const NoAssetsPage = ({}) => (
	<main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
		<div className="text-center">
			<p className="text-base font-semibold text-indigo-600">ERROR</p>
			<h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
				Asset not found
			</h1>
			<p className="mt-6 text-base leading-7 text-gray-600">
				Sorry, we couldn’t find the asset you’re looking for.
			</p>
			<div className="mt-10 flex items-center justify-center gap-x-6">
				<a
					href="/assets"
					className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
				>
					View all assets
				</a>
				<a href="#" className="text-xs font-semibold text-gray-900">
					Contact support <span aria-hidden="true">&rarr;</span>
				</a>
			</div>
		</div>
	</main>
);

export const EmptyButton = ({ text, icon, onClick, className }) => (
	<button
		type="button"
		className={classNames(
			'relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
			className,
		)}
		onClick={() => (typeof onClick === 'function' ? onClick() : null)}
	>
		<svg
			className="mx-auto h-12 w-12 text-gray-400"
			xmlns="http://www.w3.org/2000/svg"
			stroke="currentColor"
			fill="none"
			viewBox="0 0 48 48"
			aria-hidden="true"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
			/>
		</svg>
		<span className="mt-2 block text-sm font-medium text-gray-900">{text}</span>
	</button>
);

const ContentContainer = ({ children }) => (
	<div className="mt-8">{children}</div>
);

export const Content = ({ children, loading, empty, error }) => {
	if (loading) {
		return (
			<ContentContainer>
				<LoadingSkeleton />
			</ContentContainer>
		);
	}

	if (empty) {
		return (
			<ContentContainer>
				<EmptyButton {...empty} />
			</ContentContainer>
		);
	}

	if (error) {
		return (
			<ContentContainer>
				<ErrorMessage />
			</ContentContainer>
		);
	}

	return children;
};
